import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAppDispatch } from '../../../../configuration/setup/store';
import { useGetContractsQuery } from '../../api/contracts/contractApiSlice';
import { useGetAllNonLegacyProductsQuery } from '../../api/product/productApiSlice';
import type { ApiAssetInfo } from '../../api/tags/tag.types';
import { fetchAssets } from '../../api/tags/tagCalls';
import { fetchDiscountsThunk } from '../../common/discount/discounts.thunk';
import { RenewDialogContainer } from '../../common/termedLevel/dialog/RenewDialog';
import { BookingOverviewProductListContainer } from './BookingOverviewProductList';
import {
    BookingOverviewEmptyState,
    BookingOverviewErrorState,
    BookingOverviewForbiddenState,
} from './BookingOverviewStates';
import { BookingOverviewStats } from './BookingOverviewStats';
import { getBookingOverviewStatistics } from './bookingOverviewService';

export function BookingOverviewContainer() {
    const dispatch = useAppDispatch();
    const {
        data: contracts,
        error: contractsError,
        isError: hasContractsError,
        isFetching: areContractsFetching,
        refetch: refetchContracts,
    } = useGetContractsQuery(undefined);
    const {
        data: products,
        isError: hasProductsError,
        isFetching: areProductsFetching,
        refetch: refetchProducts,
    } = useGetAllNonLegacyProductsQuery();
    const [assets, setAssets] = useState<ApiAssetInfo[]>();
    const [hasAssetError, setHasAssetError] = useState(false);
    const [reloadCount, setReloadCount] = useState(0);

    const reload = () => {
        setAssets(undefined);
        setHasAssetError(false);
        setReloadCount(count => count + 1);
        refetchContracts();
        refetchProducts();
    };

    useEffect(() => {
        dispatch(fetchDiscountsThunk());
    }, [dispatch, reloadCount]);

    useEffect(() => {
        let isMounted = true;
        fetchAssets()
            .then(assets => {
                if (isMounted) {
                    setAssets(assets);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setHasAssetError(true);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [reloadCount]);

    if (hasContractsError && !areContractsFetching) {
        if (
            contractsError &&
            'status' in contractsError &&
            (contractsError.status === 401 || contractsError.status === 403)
        ) {
            return <BookingOverviewForbiddenState />;
        }

        return <BookingOverviewErrorState onReload={reload} />;
    }

    if (areContractsFetching || !contracts) {
        return <Spinner text={<FormattedMessage id='intl-msg:marketplace.loading' />} isInverse={false} />;
    }

    if (contracts.length === 0) {
        return <BookingOverviewEmptyState />;
    }

    if (hasAssetError || hasProductsError) {
        return <BookingOverviewErrorState onReload={reload} />;
    }

    if (!assets || !products || areProductsFetching) {
        return <Spinner text={<FormattedMessage id='intl-msg:marketplace.loading' />} isInverse={false} />;
    }

    return (
        <div className='padding-x-30'>
            <BookingOverviewStats statistics={getBookingOverviewStatistics(contracts, assets)} />
            <BookingOverviewProductListContainer contracts={contracts} products={products} />
            <RenewDialogContainer titleId='intl-msg:common-message.contracts.renewal' />
        </div>
    );
}
